import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FileText } from 'lucide-react';
import { humanizeKey } from '../../../utils/formatters/stringUtils';
import { formatValue, isDateField } from '../../../utils/formatters/valueUtils';
import { formatDateBr } from '../../../utils/formatters/dateUtils';
import { useReportConfig } from '../../../contexts/ReportConfigContext.jsx';
import ReportStyledField from '../ReportStyledField.jsx';

const THEMES_ORDER = [
  'uc',
  'pra',
  'biomas',
  'aldeias',
  'florestas',
  'mineracao',
  'quilombolas',
  'assentamentos',
  'embargos_ibama',
  'terras_indigenas',
];

const THEME_LABELS = {
  uc: 'UC',
  pra: 'PRA',
  biomas: 'Biomas',
  aldeias: 'Aldeias',
  florestas: 'Florestas',
  mineracao: 'Mineração',
  quilombolas: 'Quilombolas',
  assentamentos: 'Assentamentos',
  embargos_ibama: 'Embargos IBAMA',
  terras_indigenas: 'Terras Indígenas',
};

const buildGroupsByImovel = (rawList) => {
  if (!Array.isArray(rawList)) return [];

  const map = new Map();

  rawList.forEach((item) => {
    if (!item || typeof item !== 'object') return;
    const cod = item.cod_imovel || '—';
    const temas = item.temas && typeof item.temas === 'object' ? item.temas : {};
    const current = map.get(cod) || { cod_imovel: cod, temas: {} };

    THEMES_ORDER.forEach((key) => {
      const arr = Array.isArray(temas[key])
        ? temas[key].filter((t) => t && typeof t === 'object')
        : [];
      const existing = Array.isArray(current.temas[key]) ? current.temas[key] : [];
      if (arr.length > 0) {
        current.temas[key] = [...existing, ...arr];
      } else if (!current.temas[key]) {
        current.temas[key] = [];
      }
    });

    map.set(cod, current);
  });

  return Array.from(map.values());
};

const formatThemeValue = (key, value) => {
  const keyLower = String(key || '').toLowerCase();

  // Campos de data: usar helper global com heurísticas adicionais
  if (isDateField(keyLower)) {
    if (value !== null && value !== undefined && value !== '') {
      if (typeof value === 'string') {
        const raw = value.trim();

        // Apenas ano: manter como ano
        if (/^\d{4}$/.test(raw)) {
          return raw;
        }

        // dd/mm/aaaa ou dd-mm-aaaa -> normalizar para dd/mm/aaaa e usar helper
        if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(raw)) {
          const sep = raw.includes('-') ? '-' : '/';
          const [dd, mm, yyyy] = raw.split(sep);
          const normalized = `${dd}/${mm}/${yyyy}`;
          const formatted = formatDateBr(normalized);
          if (formatted) return formatted;
        }

        // mm/aaaa ou mm-aaaa
        if (/^\d{2}[-/]\d{4}$/.test(raw)) {
          const [mm, yyyy] = raw.includes('-') ? raw.split('-') : raw.split('/');
          const normalized = `01/${mm}/${yyyy}`;
          const formatted = formatDateBr(normalized);
          if (formatted) return formatted;
        }

        // aaaa/mm ou aaaa-mm
        if (/^\d{4}[-/]\d{2}$/.test(raw)) {
          const [yyyy, mm] = raw.includes('-') ? raw.split('-') : raw.split('/');
          const normalized = `01/${mm}/${yyyy}`;
          const formatted = formatDateBr(normalized);
          if (formatted) return formatted;
        }

        // 6 dígitos: tentar interpretar como AAAAMM ou MMAAAA
        if (/^\d{6}$/.test(raw)) {
          const yearFirst = parseInt(raw.slice(0, 4), 10);
          const monthLast = parseInt(raw.slice(4, 6), 10);
          if (yearFirst >= 1900 && monthLast >= 1 && monthLast <= 12) {
            const normalized = `01/${raw.slice(4, 6)}/${raw.slice(0, 4)}`;
            const formatted = formatDateBr(normalized);
            if (formatted) return formatted;
          }

          const monthFirst = parseInt(raw.slice(0, 2), 10);
          const yearLast = parseInt(raw.slice(2, 6), 10);
          if (monthFirst >= 1 && monthFirst <= 12 && yearLast >= 1900) {
            const normalized = `01/${raw.slice(0, 2)}/${raw.slice(2, 6)}`;
            const formatted = formatDateBr(normalized);
            if (formatted) return formatted;
          }
        }
      }

      const formattedDate = formatDateBr(value);
      if (formattedDate) {
        return formattedDate;
      }
    }
  }

  let display = formatValue(value);

  if (keyLower.startsWith('pct')) {
    let num;
    if (typeof value === 'number') {
      num = value;
    } else if (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      !Number.isNaN(Number(value))
    ) {
      num = Number(value);
    } else {
      num = null;
    }

    if (typeof num === 'number' && !Number.isNaN(num)) {
      const pct = Math.abs(num) <= 1 ? num * 100 : num;
      display = `${pct.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`;
    }
  }

  return display;
};

export default function SobreposicaoDomain({ data }) {
  const { isDomainEnabled, isSectionEnabled, isFieldEnabled, getFieldLabel } = useReportConfig();

  const domainEnabled = isDomainEnabled('sobreposicao');

  const groups = useMemo(() => buildGroupsByImovel(data), [data]);

  const groupsWithData = useMemo(() => {
    if (!domainEnabled) return [];
    return groups
      .map((group) => {
        const temas = group.temas || {};
        const enabledThemes = THEMES_ORDER.filter(
          (key) =>
            isSectionEnabled('sobreposicao', key) &&
            Array.isArray(temas[key]) &&
            temas[key].length > 0,
        );
        if (enabledThemes.length === 0) {
          return null;
        }
        return {
          ...group,
          enabledThemes,
        };
      })
      .filter(Boolean);
  }, [groups, domainEnabled, isSectionEnabled]);

  if (!domainEnabled) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Sobreposição
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Domínio Sobreposição desabilitado na configuração do relatório.
        </Typography>
      </Box>
    );
  }

  if (!groupsWithData.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Sobreposição
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Nenhum dado de Sobreposições disponível para esta propriedade.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <FileText size={20} style={{ marginRight: 8 }} />
        <Typography variant="h6">Sobreposições de Temas</Typography>
      </Box>

      {groupsWithData.map((group, index) => (
        <Accordion key={group.cod_imovel || index} defaultExpanded={index === 0} sx={{ mb: 1.5 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Código do imóvel: {group.cod_imovel || '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {group.enabledThemes.length === 1
                  ? '1 tema com dados'
                  : `${group.enabledThemes.length} temas com dados`}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {group.enabledThemes.map((themeKey) => {
                const registros = Array.isArray(group.temas?.[themeKey])
                  ? group.temas[themeKey]
                  : [];
                const themeLabel = getFieldLabel(
                  'sobreposicao',
                  themeKey,
                  THEME_LABELS[themeKey] || themeKey,
                );

                return (
                  <Accordion key={themeKey} disableGutters sx={{ mb: 0.5 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {themeLabel}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {registros.length === 1 ? '1 registro' : `${registros.length} registros`}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {registros.map((item, itemIndex) => {
                          const entries = Object.entries(item || {})
                            .filter(
                              ([field]) =>
                                field !== 'geojson' && field !== 'geometry' && field !== 'feature',
                            )
                            .filter(([field]) => isFieldEnabled('sobreposicao', themeKey, field));

                          return (
                            <Card
                              key={`${themeKey}-${itemIndex}`}
                              variant="outlined"
                              sx={{
                                mb: 1,
                                borderRadius: 2,
                                boxShadow: 0,
                                borderLeft: 3,
                                borderLeftColor: 'primary.main',
                                bgcolor: 'grey.50',
                              }}
                            >
                              <CardContent>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                  {`${getFieldLabel('sobreposicao', themeKey, THEME_LABELS[themeKey] || themeKey)} • Item ${itemIndex + 1}`}
                                </Typography>
                                {entries.length === 0 ? (
                                  <Typography variant="body2" color="text.secondary">
                                    Sem detalhes disponíveis para este registro.
                                  </Typography>
                                ) : (
                                  <Grid container spacing={1.5}>
                                    {entries.map(([field, value]) => (
                                      <Grid item xs={12} sm={6} md={4} key={field}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          {getFieldLabel('sobreposicao', field, humanizeKey(field))}
                                        </Typography>
                                        <ReportStyledField
                                          domainKey="sobreposicao"
                                          fieldKey={field}
                                          variant="body2"
                                          defaultSx={{ fontWeight: 500 }}
                                        >
                                          {formatThemeValue(field, value)}
                                        </ReportStyledField>
                                      </Grid>
                                    ))}
                                  </Grid>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      <Divider sx={{ mt: 2 }} />
    </Box>
  );
}
